/**
 * component-search.js -- Predictive Search overlay Web Component for Mirai Online (Phase 5)
 *
 * <predictive-search> custom element:
 *  - Full-screen takeover overlay opened from the header [data-search-trigger] button
 *  - Default state (server-rendered): curated popular searches + four collection links
 *  - On input >= 2 chars (200ms debounce): fetch /search/suggest.json and render a two-zone
 *    layout — product results (thumbnail + title + price) + query suggestions
 *  - "View all results" link + form submit navigate to /search?q={query}
 *  - ARIA dialog: role="dialog", aria-modal, focus trap (re-queried every Tab), inert
 *    background, ESC / backdrop / close button close, focus returned to trigger on close
 *  - Theme editor compatible via shopify:section:load reinit
 *  - Light DOM only (no Shadow DOM)
 *
 * Security notes (per threat model):
 *  - T-05-01 (XSS): the user query is rendered with textContent ONLY (never innerHTML)
 *    in the "No results for '{q}'" path and the "View all results" label.
 *  - T-05-02: queries[].styled_text is rendered via innerHTML — this is SAFE: it is
 *    Shopify-platform-generated markup containing only <mark>/<span> highlight tags,
 *    not user input (documented known-safe injection, like Section Rendering HTML).
 *  - T-05-03 (DoS): 200ms debounce + AbortController cancels in-flight requests on new
 *    input; AbortError is swallowed; a non-ok response (e.g. 429) returns without retry.
 */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this._boundKeydown = this._handleKeydown.bind(this);
    this._isOpen = false;
    this._lastFocused = null;
    this._releaseFocusTrap = null;
    this._abortController = null;
    this._debounceTimer = null;
    this._currentQuery = '';
    this._activeIndex = -1;
  }

  connectedCallback() {
    this._init();
    // Listen on document — the overlay is a static section in theme.liquid, but the
    // relevant editor reload may be the predictive-search or header section (Open Q1).
    this._boundSectionLoad = (e) => {
      const id = e.detail && e.detail.sectionId;
      if (id && (id.indexOf('predictive-search') !== -1 || id.indexOf('header') !== -1)) {
        this._reinit();
      }
    };
    document.addEventListener('shopify:section:load', this._boundSectionLoad);
  }

  disconnectedCallback() {
    this._teardown();
    document.removeEventListener('shopify:section:load', this._boundSectionLoad);
  }

  _init() {
    this._inner = this.querySelector('.search-overlay__inner');
    this._input = this.querySelector('[data-search-input]');
    this._clearBtn = this.querySelector('[data-search-clear]');
    this._closeBtn = this.querySelector('[data-search-close]');
    this._defaultEl = this.querySelector('[data-search-default]');
    this._resultsEl = this.querySelector('[data-search-results]');
    this._resultsListEl = this.querySelector('#search-results-list');
    this._backdrop = this.previousElementSibling &&
      this.previousElementSibling.matches('[data-search-backdrop]')
        ? this.previousElementSibling
        : document.querySelector('[data-search-backdrop]');

    this._onInput = this._handleInput.bind(this);
    this._onClose = () => this.close();
    this._onClear = this._handleClear.bind(this);

    if (this._input) this._input.addEventListener('input', this._onInput);
    if (this._clearBtn) this._clearBtn.addEventListener('click', this._onClear);
    if (this._closeBtn) this._closeBtn.addEventListener('click', this._onClose);
    if (this._backdrop) this._backdrop.addEventListener('click', this._onClose);
    document.addEventListener('keydown', this._boundKeydown);
  }

  _teardown() {
    if (this._isOpen) this.close();
    clearTimeout(this._debounceTimer);
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    if (this._input) this._input.removeEventListener('input', this._onInput);
    if (this._clearBtn) this._clearBtn.removeEventListener('click', this._onClear);
    if (this._closeBtn) this._closeBtn.removeEventListener('click', this._onClose);
    if (this._backdrop) this._backdrop.removeEventListener('click', this._onClose);
    document.removeEventListener('keydown', this._boundKeydown);
    if (this._releaseFocusTrap) {
      this._releaseFocusTrap();
      this._releaseFocusTrap = null;
    }
  }

  _reinit() {
    this._teardown();
    this._init();
  }

  get isOpen() {
    return this._isOpen;
  }

  /**
   * Open the overlay.
   * @param {Element} [triggerEl] - element to return focus to on close
   */
  open(triggerEl) {
    if (this._isOpen) return;
    this._lastFocused = triggerEl || document.activeElement;

    this.setAttribute('aria-hidden', 'false');
    if (this._backdrop) this._backdrop.setAttribute('aria-hidden', 'false');
    window.Theme.lockScroll();
    document.querySelector('#MainContent')?.setAttribute('inert', '');
    document.querySelector('#header-group')?.setAttribute('inert', '');

    this._isOpen = true;
    this._showDefaultState();
    this._releaseFocusTrap = this._trapFocus();

    // Autofocus the input after the open transition begins
    if (this._input) this._input.focus();
  }

  close() {
    if (!this._isOpen) return;

    this.setAttribute('aria-hidden', 'true');
    if (this._backdrop) this._backdrop.setAttribute('aria-hidden', 'true');
    window.Theme.unlockScroll();
    document.querySelector('#MainContent')?.removeAttribute('inert');
    document.querySelector('#header-group')?.removeAttribute('inert');

    this._isOpen = false;
    clearTimeout(this._debounceTimer);
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }

    if (this._releaseFocusTrap) {
      this._releaseFocusTrap();
      this._releaseFocusTrap = null;
    }
    if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
      this._lastFocused.focus();
    }
    this._lastFocused = null;
  }

  _handleKeydown(e) {
    if (!this._isOpen) return;

    if (e.key === 'Escape') {
      this.close();
      return;
    }

    // The input advertises aria-autocomplete="list" + aria-controls on a
    // role="listbox", but nothing implemented it: ArrowDown did nothing and there
    // was no way to reach a result without a mouse.
    const options = this._resultOptions();
    if (!options.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this._setActiveOption((this._activeIndex + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this._setActiveOption((this._activeIndex - 1 + options.length) % options.length);
    } else if (e.key === 'Enter' && this._activeIndex > -1) {
      e.preventDefault();
      options[this._activeIndex].click();
    }
  }

  _resultOptions() {
    if (!this._resultsListEl) return [];
    return Array.from(this._resultsListEl.querySelectorAll('[role="option"]'));
  }

  /** Roving aria-activedescendant — focus stays in the input, as a combobox requires. */
  _setActiveOption(index) {
    const options = this._resultOptions();
    this._activeIndex = index;

    options.forEach((opt, i) => {
      const isActive = i === index;
      opt.setAttribute('aria-selected', isActive ? 'true' : 'false');
      opt.classList.toggle('search-result--active', isActive);
      if (isActive) {
        opt.scrollIntoView({ block: 'nearest' });
        if (this._input) this._input.setAttribute('aria-activedescendant', opt.id);
      }
    });

    if (index === -1 && this._input) this._input.removeAttribute('aria-activedescendant');
  }

  _handleClear() {
    if (this._input) {
      this._input.value = '';
      this._input.focus();
    }
    this._currentQuery = '';
    if (this._clearBtn) this._clearBtn.hidden = true;
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    this._showDefaultState();
  }

  _handleInput() {
    const q = (this._input.value || '').trim();
    this._currentQuery = q;
    if (this._clearBtn) this._clearBtn.hidden = q.length === 0;

    if (q.length < 2) {
      clearTimeout(this._debounceTimer);
      if (this._abortController) {
        this._abortController.abort();
        this._abortController = null;
      }
      this._showDefaultState();
      return;
    }

    this._showResultsState();

    // Only paint the skeleton when there is nothing on screen to preserve.
    // _showSkeleton() wipes the list, and it ran synchronously on EVERY keystroke
    // — before the 200ms debounce had even fired — so typing "saree" blanked the
    // results the shopper was already reading and flashed grey bars between each
    // letter. The debounce protected the network but not the DOM.
    if (!this._resultOptions().length) {
      this._showSkeleton();
    } else if (this._resultsListEl) {
      this._resultsListEl.setAttribute('aria-busy', 'true');
    }

    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._performSearch(q), 200);
  }

  async _performSearch(query) {
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,query',
      'resources[limit]': 4,
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'title,variants.title',
      'resources[options][unavailable_products]': 'hide'
    });

    const root = (window.Theme && window.Theme.routes && window.Theme.routes.root) || '/';
    const base = root.endsWith('/') ? root.slice(0, -1) : root;

    try {
      const res = await fetch(`${base}/search/suggest.json?${params}`, {
        signal: this._abortController.signal
      });
      // Non-ok (e.g. 429 rate limit) — bail without retry storm (T-05-03)
      if (!res.ok) return;
      const data = await res.json();
      const results = (data && data.resources && data.resources.results) || {};
      this._renderResults(results, query);
    } catch (e) {
      if (e.name !== 'AbortError') console.warn('[PredictiveSearch]', e);
    }
  }

  _showDefaultState() {
    if (this._defaultEl) this._defaultEl.hidden = false;
    if (this._resultsEl) this._resultsEl.hidden = true;
  }

  _showResultsState() {
    if (this._defaultEl) this._defaultEl.hidden = true;
    if (this._resultsEl) this._resultsEl.hidden = false;
  }

  _showSkeleton() {
    if (!this._resultsListEl) return;
    this._resultsListEl.textContent = '';
    const wrap = document.createElement('div');
    wrap.className = 'search-overlay__zone';
    for (let i = 0; i < 3; i++) {
      const row = document.createElement('div');
      row.className = 'search-result search-result--skeleton';
      const thumb = document.createElement('span');
      thumb.className = 'search-result__thumb search-skeleton';
      const text = document.createElement('span');
      text.className = 'search-result__text';
      const bar1 = document.createElement('span');
      bar1.className = 'search-skeleton search-skeleton--line';
      const bar2 = document.createElement('span');
      bar2.className = 'search-skeleton search-skeleton--line search-skeleton--short';
      text.appendChild(bar1);
      text.appendChild(bar2);
      row.appendChild(thumb);
      row.appendChild(text);
      wrap.appendChild(row);
    }
    this._resultsListEl.appendChild(wrap);
  }

  _renderResults(results, query) {
    if (!this._resultsListEl) return;
    // Ignore stale renders for an outdated query
    if (query !== this._currentQuery) return;

    const products = (results.products || []).slice(0, 4);
    const queries = (results.queries || []).slice(0, 6);

    this._resultsListEl.textContent = '';
    this._resultsListEl.removeAttribute('aria-busy');
    // The rows we are about to replace are gone — drop any roving selection with
    // them, or aria-activedescendant points at a detached id.
    this._setActiveOption(-1);

    const layout = document.createElement('div');
    layout.className = 'search-overlay__zones';

    // ---- Product zone ----
    const productZone = document.createElement('div');
    productZone.className = 'search-overlay__zone search-overlay__zone--products';

    const productHeading = document.createElement('h3');
    productHeading.className = 'search-overlay__zone-heading';
    productHeading.textContent = this._t('products', 'Products');
    productZone.appendChild(productHeading);

    if (products.length === 0) {
      const noRes = document.createElement('p');
      noRes.className = 'search-overlay__no-results';
      // T-05-01: user query rendered with textContent ONLY — never innerHTML
      noRes.textContent = `No results for "${query}"`;
      productZone.appendChild(noRes);
    } else {
      const list = document.createElement('div');
      list.className = 'search-overlay__product-list';
      products.forEach((p, i) => list.appendChild(this._buildProductRow(p, i)));
      productZone.appendChild(list);
      this._activeIndex = -1;

      // "View all results" link
      const root = (window.Theme && window.Theme.routes && window.Theme.routes.root) || '/';
      const base = root.endsWith('/') ? root.slice(0, -1) : root;
      const viewAll = document.createElement('a');
      viewAll.className = 'search-overlay__view-all';
      viewAll.href = `${base}/search?q=${encodeURIComponent(query)}`;
      // textContent (not innerHTML) — query echoed safely (T-05-01)
      viewAll.textContent = `View all results for "${query}" →`;
      productZone.appendChild(viewAll);
    }

    // ---- Suggestions zone ----
    const suggestZone = document.createElement('div');
    suggestZone.className = 'search-overlay__zone search-overlay__zone--suggestions';

    if (queries.length > 0) {
      const suggestHeading = document.createElement('h3');
      suggestHeading.className = 'search-overlay__zone-heading';
      suggestHeading.textContent = this._t('popular_searches', 'Popular searches');
      suggestZone.appendChild(suggestHeading);

      const tags = document.createElement('div');
      tags.className = 'search-overlay__suggestions';
      queries.forEach((qObj) => {
        const tag = document.createElement('a');
        tag.className = 'search-overlay__tag';
        tag.href = qObj.url || '#';
        // T-05-02: styled_text is Shopify-generated markup (<mark>/<span> only) — safe
        // innerHTML injection. Fall back to textContent when styled_text is absent.
        if (qObj.styled_text) {
          tag.innerHTML = qObj.styled_text;
        } else {
          tag.textContent = qObj.text || '';
        }
        tags.appendChild(tag);
      });
      suggestZone.appendChild(tags);
    }

    layout.appendChild(productZone);
    if (queries.length > 0) layout.appendChild(suggestZone);
    this._resultsListEl.appendChild(layout);
  }

  _buildProductRow(product, index) {
    const root = (window.Theme && window.Theme.routes && window.Theme.routes.root) || '/';
    const row = document.createElement('a');
    row.className = 'search-result';
    row.href = product.url || '#';
    // The container declares role="listbox" and the input aria-controls it, but the
    // rows were plain <a> with no role — a screen reader announced a listbox with
    // zero options. These ids also back aria-activedescendant for arrow-key nav.
    row.setAttribute('role', 'option');
    row.setAttribute('aria-selected', 'false');
    row.id = `search-result-${index}`;

    const thumb = document.createElement('span');
    thumb.className = 'search-result__thumb';
    if (product.featured_image && product.featured_image.url) {
      const img = document.createElement('img');
      img.src = product.featured_image.url;
      img.alt = product.featured_image.alt || product.title || '';
      img.loading = 'lazy';
      img.width = 48;
      img.height = 64;
      thumb.appendChild(img);
    }
    row.appendChild(thumb);

    const text = document.createElement('span');
    text.className = 'search-result__text';

    const title = document.createElement('span');
    title.className = 'search-result__title';
    title.textContent = product.title || '';
    text.appendChild(title);

    const priceWrap = document.createElement('span');
    priceWrap.className = 'search-result__price';
    const price = document.createElement('span');
    price.className = 'search-result__current-price';
    price.textContent = this._formatMoney(product.price);
    priceWrap.appendChild(price);

    if (product.compare_at_price_min && product.compare_at_price_min > product.price) {
      const compare = document.createElement('s');
      compare.className = 'search-result__compare-price';
      compare.textContent = this._formatMoney(product.compare_at_price_min);
      priceWrap.appendChild(compare);
    }
    text.appendChild(priceWrap);

    row.appendChild(text);
    return row;
  }

  /**
   * Format a price. The predictive API returns prices as pre-formatted strings
   * (e.g. "Rs. 12,999.00") when fields are requested, but can also return cents
   * integers. Handle both defensively.
   */
  _formatMoney(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    // Numeric cents → currency string via window.Theme money formatter fallback
    const amount = (value / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    const currency = (window.Theme && window.Theme.settings && window.Theme.settings.currency) || 'INR';
    const symbol = currency === 'INR' ? '₹' : '';
    return `${symbol}${amount}`;
  }

  _t(key, fallback) {
    const strings = (window.Theme && window.Theme.strings) || {};
    return strings[`search_${key}`] || fallback;
  }

  _trapFocus() {
    const focusableSelectors =
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    // Re-query on every Tab — content changes as results render (never cache)
    const getFocusable = () =>
      Array.from(this.querySelectorAll(focusableSelectors)).filter((el) => !el.hidden && el.offsetParent !== null);

    const handleKeydown = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    this.addEventListener('keydown', handleKeydown);
    return () => this.removeEventListener('keydown', handleKeydown);
  }
}

// Registration guard: only define once even if the section renders multiple times
if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
